import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Scale, Lock, Crown, Clock, Sparkles } from "lucide-react";
import Nav from "@/components/Nav";
import api from "@/lib/api";

const CLASS_STYLE = {
  Perfect: { color: "#FFE600", ring: "border-[#FFE600]/40", bg: "bg-[#FFE600]/10" },
  Heavy: { color: "#00F0FF", ring: "border-[#00F0FF]/40", bg: "bg-[#00F0FF]/10" },
  Balanced: { color: "#7A5CFF", ring: "border-[#7A5CFF]/40", bg: "bg-[#7A5CFF]/10" },
  Light: { color: "#52525B", ring: "border-white/10", bg: "bg-white/5" },
};

const DeviationMap = ({ map, label }) => (
  <div className="min-w-0">
    <div className="font-mono-sig text-[9px] tracking-[0.25em] uppercase text-[#52525B] mb-1">{label}</div>
    <div className="font-constitution text-base sm:text-lg flex flex-wrap gap-x-2 gap-y-1">
      {(map.match(/.{1,4}/g) || []).map((group, gi) => (
        <span key={gi} className="inline-flex">
          {group.split("").map((ch, i) => (
            <span key={i} className={ch === "1" ? "deviation-glow" : "static-dot opacity-60"}>
              {ch}
            </span>
          ))}
        </span>
      ))}
    </div>
  </div>
);

export default function Ledger() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => api.get("/ledger").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 5000); // live refresh
    return () => clearInterval(t);
  }, []);

  const totals = data?.totals;
  const entries = data?.entries || [];

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white grain">
      <Nav active="ledger" />
      <main className="max-w-6xl mx-auto px-5 md:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[#FFE600]/15 border border-[#FFE600]/30 flex items-center justify-center">
              <Scale className="w-5 h-5 text-[#FFE600]" />
            </div>
            <div>
              <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">Constitution Ledger</h1>
              <p className="font-tech text-sm text-[#A0A0AB]">The law of static &amp; movement — mass is uniformity, randomness is lightness.</p>
            </div>
          </div>

          <p className="font-tech text-sm text-[#7A5CFF] max-w-2xl mb-8 mt-3 border-l-2 border-[#7A5CFF]/40 pl-4">
            Which is stronger — a perfect circle down to the point, or a single deviation? The ledger keeps the count; the answer is yours.
          </p>

          {/* Live balance ledger */}
          <div data-testid="ledger-live-balance" className="rounded-2xl border border-[#00FF9D]/25 bg-[#00FF9D]/[0.05] p-5 mb-6 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-[#00FF9D] animate-pulse" />
              <span className="font-mono-sig text-[10px] tracking-[0.3em] uppercase text-[#A0A0AB]">Live balance on the network</span>
            </div>
            <div className="flex items-center gap-8">
              <div>
                <p className="font-mono-sig text-[9px] tracking-widest uppercase text-[#52525B]">USD held</p>
                <p data-testid="live-balance-usd" className="font-display font-black text-2xl sm:text-3xl text-[#00FF9D] tabular-nums">
                  ${Number(totals?.balances?.USD ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="font-mono-sig text-[9px] tracking-widest uppercase text-[#52525B]">EUR held</p>
                <p data-testid="live-balance-eur" className="font-display font-black text-2xl sm:text-3xl text-[#00F0FF] tabular-nums">
                  €{Number(totals?.balances?.EUR ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-10" data-testid="ledger-totals">
            <StatCard label="Value Upheld" value={totals?.value_upheld ?? 0} accent="#FFE600" big />
            <StatCard label="Cards" value={totals?.card_count ?? 0} accent="#00F0FF" />
            <StatCard label="Perfect" value={totals?.class_counts?.Perfect ?? 0} accent="#FFE600" />
            <StatCard label="Heavy" value={totals?.class_counts?.Heavy ?? 0} accent="#00F0FF" />
            <StatCard label="Light" value={totals?.class_counts?.Light ?? 0} accent="#52525B" />
          </div>

          <div className="space-y-3" data-testid="ledger-list">
            {entries.length === 0 && (
              <p className="text-center text-[#52525B] py-16">No cards registered on the ledger yet.</p>
            )}
            {entries.map((e) => {
              const cs = CLASS_STYLE[e.class] || CLASS_STYLE.Light;
              return (
                <motion.div
                  key={e.card_id}
                  data-testid={`ledger-row-${e.card_id}`}
                  whileHover={{ y: -2 }}
                  className={`rounded-2xl border ${cs.ring} ${cs.bg} p-5`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-4">
                      <div className="font-display font-black text-2xl w-10 text-center" style={{ color: cs.color }}>
                        {e.rank === 1 ? <Crown className="w-6 h-6 mx-auto" /> : `#${e.rank}`}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono-sig text-[#00F0FF]">{e.nickname}</span>
                          {e.locked && (
                            <span data-testid={`ledger-locked-${e.card_id}`} className="inline-flex items-center gap-1 text-[10px] font-mono-sig uppercase tracking-wider text-[#FF007F] border border-[#FF007F]/40 rounded-full px-2 py-0.5">
                              <Lock className="w-3 h-3" /> Reserved
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-[#A0A0AB]">{e.cardholder_name}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-black text-3xl" style={{ color: cs.color }}>{e.score}</div>
                      <div className="font-mono-sig text-[10px] uppercase tracking-[0.2em]" style={{ color: cs.color }}>{e.class}</div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 mt-4 rounded-xl bg-black/30 border border-white/5 p-4">
                    <DeviationMap map={e.static_map} label={`Static · ${e.static_deviations} dev`} />
                    <DeviationMap map={e.moving_map} label={`Moving · ${e.moving_deviations} dev`} />
                  </div>

                  <div className="flex items-center gap-5 mt-3 font-mono-sig text-[11px] text-[#A0A0AB]">
                    <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#FFE600]" /> {e.total_deviations} deviations</span>
                    <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#00F0FF]" /> held {e.held_days}d</span>
                    <span className="inline-flex items-center gap-1.5">toughness <b style={{ color: cs.color }}>{e.toughness}</b></span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </main>
    </div>
  );
}

const StatCard = ({ label, value, accent, big }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
    <div className="font-mono-sig text-[9px] tracking-[0.25em] uppercase text-[#52525B] mb-1">{label}</div>
    <div className={`font-display font-black ${big ? "text-3xl" : "text-2xl"}`} style={{ color: accent }}>{value}</div>
  </div>
);
